import { Component, ElementRef, OnInit, QueryList, ViewChildren, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { NgClass, NgIf, NgStyle } from '@angular/common';
import { PortalAdminService } from '../../core/services/api-services/portal-admin.service';
import { Router } from '@angular/router';
import { AuthService } from '../../core/services/client-services/auth.service';
import { LoginResponse } from '../../core/models/login-response.model';
import { HttpErrorResponse } from '@angular/common/http';
import { APP_VERSION } from '../../../environments/config';
import { Subscription } from 'rxjs';
import { SignalRService } from '../../core/services/client-services/signalr.service';

declare var bootstrap: any;

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [FormsModule, NgClass, NgIf, NgStyle],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {

  appVersion = APP_VERSION;
  currentYear = new Date().getFullYear();

  // Form fields
  username: string = '';
  password: string = '';
  pin: string = '';
  showPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  // OTP handling
  otp: string = '';
  otpControls = new Array(6);
  showOtpSection: boolean = false;

  // Timer state
  time: number = 60;
  private intervalId: any;
  running: boolean = false;

  // Temporarily stored credentials for resending OTP
  private storedPassword: string = '';
  private storedPin: string = '';

  // NEW: Forgot password state
  showForgotSection = false;
  showForgotOtpSection = false;
  forgotEmail = '';
  newPassword = '';
  confirmPassword = '';
  forgotOtp = '';

  // View references
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;
  @ViewChild('usernameInput') usernameInputRef!: ElementRef;
  @ViewChild('otpField') otpField!: ElementRef;
  @ViewChild('forgotOtpField') forgotOtpField!: ElementRef;  // NEW

  dataSubscriber$!: Subscription;

  constructor(
    private toaster: ToastrService,
    private portalAdminService: PortalAdminService,
    private router: Router,
    private authService: AuthService,
    private signalrService: SignalRService
  ) {
    this.dataSubscriber$ = new Subscription();
  }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    // Focus the username input if OTP section is hidden
    if (!this.showOtpSection) {
      setTimeout(() => {
        this.usernameInputRef.nativeElement.focus();
      });
    }
  }

  // Starts the 60-second countdown timer for OTP
  private startTimer(): void {
    if (!this.running) {
      this.running = true;
      this.time = 60;
      this.intervalId = setInterval(() => {
        if (this.time > 0) {
          this.time--;
        } else {
          this.stopTimer();
        }
      }, 1000);
    }
  }

  // Stops the OTP countdown timer
  private stopTimer(): void {
    this.running = false;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // Toggles password visibility
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  // Toggles new password visibility
  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  // Toggles confirm password visibility
  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  // Handles initial login form submission and requests OTP
  adminlogin(f: NgForm): void {
    const username = f.value.username?.trim();
    const password = f.value.password?.trim();
    const pin = f.value.pin?.trim();

    // 1. Check if both username and password are empty
    if (!username && !password) {
      this.toaster.error('Please enter your email/username and password to log in.', 'Credentials Required');
      return;
    }

    // 2. Check username specifically
    if (!username) {
      this.toaster.error('Please enter your email or username.', 'Missing Username');
      return;
    }

    // 3. Check password specifically
    if (!password) {
      this.toaster.error('Please enter your password to log in.', 'Password Required');
      return;
    }

    // 4. Check password length (only if password exists)
    const passwordLength = password.length;

    if (passwordLength > 0 && passwordLength < 6) {
      this.toaster.error('Password must be at least 6 characters or more.', 'Password Too Short');
      return;
    }


    if (passwordLength === 0) {
      this.toaster.error('Please enter your password to log in.', 'Credentials Required');
      return;
    }

    if (passwordLength > 0 && passwordLength < 6) {
      this.toaster.error('Password must be at least 6 characters long.', 'Password Too Short');
      return;
    }


    const loginPayload = {
      emailOrUserName: username,
      password: password || '',
      pin: pin || ''
    };
    this.dataSubscriber$.add(
      this.portalAdminService.loginRequestOtp(loginPayload).subscribe({
        next: (response) => {
          // Display server message if available
          const successMessage = response?.data?.message || response?.message || 'OTP code sent to your email.';
          this.toaster.success(successMessage, 'OTP Sent');

          // Store credentials for resending OTP
          this.storedPassword = password || '';
          this.storedPin = pin || '';
          this.username = username;

          // Show OTP section and start timer
          this.showOtpSection = true;
          this.startTimer();

          // Focus on OTP input
          setTimeout(() => {
            this.otpField?.nativeElement?.focus();
          });
        },
        error: (err: HttpErrorResponse) => {
          let errorMessage = 'An error occurred. Please try again.';

          // Handle JSON response
          if (err.error && typeof err.error === 'object') {
            errorMessage = err.error.message || err.error.error || errorMessage;
          }
          // Handle plain text response
          else if (typeof err.error === 'string' && err.error.trim()) {
            // Try to parse if it looks like JSON
            try {
              const parsed = JSON.parse(err.error);
              errorMessage = parsed.message || parsed.error || err.error.trim();
            } catch {
              // It's plain text
              errorMessage = err.error.trim();
            }
          }

          this.toaster.error(errorMessage, 'LOGIN FAILED');
        }
      })
    );
  }

  // Confirms the OTP and completes the login
  confirmOtp(): void {
    if (!this.otp || this.otp.length < 6) {
      this.toaster.error('Enter the OTP sent to your email.', 'OTP Required');
      return;
    }

    const otpPayload = {
      emailOrUserName: this.username,
      otp: this.otp
    };
    this.dataSubscriber$.add(
      this.portalAdminService.confirmOtp(otpPayload).subscribe({
        next: (res: LoginResponse) => {
          const token = res.data.jwt;

          if (token) {
            this.authService.setToken(token);
            this.authService.setUser(res.data.admin);
            this.authService.setAccessControl(res.data.accessMatrix, res.data.roleKey, res.data.roleName);
            this.toaster.success('OTP verified successfully.', 'Login Successful');
            this.signalrService.startConnection();
            this.router.navigate(['/dashboard']);
          } else {
            this.toaster.error('Token not received from server.', 'Login Failed');
          }
        },
        error: (err) => {
          const statusCode = err.status;

          if (statusCode === 401) {
            this.toaster.error('Invalid Credentials.', 'Unauthorized');
            this.showOtpSection = false;
            this.otp = '';
            this.stopTimer();
            this.time = 0;
            this.storedPassword = '';
            this.storedPin = '';
          } else {
            this.toaster.error(`OTP verification failed (${statusCode})`, 'Try again');
          }
        }
      })
    );
  }

  // Resends OTP using stored credentials
  resendOtp(): void {
    if (!this.username) {
      this.toaster.error('Missing username', 'Cannot resend OTP without username');
      return;
    }

    const payload = {
      emailOrUserName: this.username,
      password: this.storedPassword,
      pin: this.storedPin
    };
    this.dataSubscriber$.add(
      this.portalAdminService.loginRequestOtp(payload).subscribe({
        next: () => {
          this.toaster.success('A new OTP has been sent to your email.', 'OTP Resent');
          this.otp = '';
          this.time = 60;
          this.startTimer();

          setTimeout(() => {
            this.otpField?.nativeElement?.focus();
          });
        },
        error: () => {
          this.toaster.error('Failed to resend OTP', 'Try again.');
        }
      })
    );
  }

  /* ================================
     NEW: Forgot Password methods
     (appended at the end)
     ================================ */

  openForgotPassword(): void {
    this.resetAllOtpState();
    this.showForgotSection = true;
    this.showForgotOtpSection = false;
    // Prefill email if username looks like an email
    if (this.username?.includes('@')) this.forgotEmail = this.username;
  }

  cancelForgot(): void {
    this.showForgotSection = false;
    this.showForgotOtpSection = false;
    this.forgotEmail = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.forgotOtp = '';
    this.stopTimer();
    this.time = 0;
  }

  // submitForgot(f: NgForm): void {
  //   if (!this.forgotEmail) {
  //     this.toaster.error('Please enter your email.', 'EMAIL REQUIRED');
  //     return;
  //   }
  //   if (!this.newPassword || this.newPassword.length < 6) {
  //     this.toaster.error('Password must be at least 6 characters.', 'WEAK PASSWORD');
  //     return;
  //   }
  //   if (this.newPassword !== this.confirmPassword) {
  //     this.toaster.error('Passwords do not match.', 'CONFIRM PASSWORD');
  //     return;
  //   }

  //   const payload = { email: this.forgotEmail.trim(), newPassword: this.newPassword };
  //   this.portalAdminService.requestPasswordReset(payload).subscribe({
  //     next: (response) => {
  //       this.toaster.success('OTP sent to your email.', 'RESET OTP SENT');
  //       this.showForgotOtpSection = true;
  //       this.startTimer();
  //       setTimeout(() => this.forgotOtpField?.nativeElement?.focus());
  //     },
  //     error: (err) => {
  //       this.toaster.error(err?.error.message || err?.message || 'Failed to start password reset.', 'TRY AGAIN');
  //     }
  //   });
  // }

  submitForgot(f: NgForm): void {

    const email = this.forgotEmail?.trim();
    const newPassword = this.newPassword?.trim();
    const confirmPassword = this.confirmPassword?.trim();

    // 1. Email validation
    if (!email) {
      this.toaster.warning('Please enter your email address.', 'Email Required');
      this.focusField('forgotEmailInput');
      return;
    }

    if (!this.isValidEmail(email)) {
      this.toaster.error('Please enter a valid email address.', 'Invalid Email');
      this.focusField('forgotEmailInput');
      return;
    }

    // 2. New Password validation
    if (!newPassword) {
      this.toaster.warning('Please enter a new password.', 'Password Required');
      this.focusField('newPasswordInput');
      return;
    }

    if (newPassword.length < 6) {
      this.toaster.error('Password must be at least 6 characters long.', 'Password Too Short');
      this.focusField('newPasswordInput');
      return;
    }

    // 3. Confirm Password
    if (newPassword !== confirmPassword) {
      this.toaster.error('The passwords you entered do not match.', 'Passwords Don’t Match');
      this.focusField('confirmPasswordInput');
      return;
    }

    // All client-side checks passed → send request
    const payload = { email, newPassword };
    this.dataSubscriber$.add(
      this.portalAdminService.requestPasswordReset(payload).subscribe({
        next: (response) => {
          this.handleResetSuccess();
        },
        error: (err) => {
          // Even on error (404, 500, etc.), we show success to prevent enumeration
          this.handleResetSuccess();
        }
      })
    );
  }

  // Helper: Always show success and proceed
  private handleResetSuccess(): void {
    this.toaster.success(
      'If an account exists with that email, an OTP has been sent.',
      'Check Your Email'
    );

    // Proceed to OTP step (feels instant and professional)
    this.showForgotOtpSection = true;
    this.startTimer();

    // Auto-focus OTP input
    setTimeout(() => {
      this.forgotOtpField?.nativeElement?.focus();
    }, 300);
  }

  // Email format validator
  private isValidEmail(email: string): boolean {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  }

  // Auto-focus wrong field (premium UX)
  private focusField(refName: string): void {
    setTimeout(() => {
      const el = (this as any)[refName]?.nativeElement;
      if (el) {
        el.focus();
        el.select?.();
      }
    }, 100);
  }

  confirmForgotOtp(): void {
    if (!this.forgotOtp || this.forgotOtp.length < 6) {
      this.toaster.error('Enter the OTP sent to your email.', 'OTP REQUIRED');
      return;
    }

    const payload = { email: this.forgotEmail.trim(), otp: this.forgotOtp };
    this.dataSubscriber$.add(
      this.portalAdminService.confirmPasswordReset(payload).subscribe({
        next: () => {
          this.toaster.success('Password reset successfully. Please log in with your new password.', 'RESET SUCCESS');
          // Go back to login
          this.cancelForgot();
          this.showOtpSection = false;
          this.username = this.forgotEmail;
          setTimeout(() => this.usernameInputRef?.nativeElement?.focus());
        },
        error: (err: HttpErrorResponse) => {
          let errorMessage = 'An error occurred. Please try again.';

          // Handle JSON response
          if (err.error && typeof err.error === 'object') {
            errorMessage = err.error.message || err.error.error || errorMessage;
          }
          // Handle plain text response
          else if (typeof err.error === 'string' && err.error.trim()) {
            // Try to parse if it looks like JSON
            try {
              const parsed = JSON.parse(err.error);
              errorMessage = parsed.message || parsed.error || err.error.trim();
            } catch {
              // It's plain text
              errorMessage = err.error.trim();
            }
          }

          this.toaster.error(errorMessage, 'OTP VERIFICATION FAILED');
        }
      })
    );
  }

  resendForgotOtp(): void {
    if (!this.forgotEmail || !this.newPassword) {
      this.toaster.error('Email and New Password are required to resend OTP.', 'MISSING FIELDS');
      return;
    }
    const payload = { email: this.forgotEmail.trim(), newPassword: this.newPassword };
    this.dataSubscriber$.add(
      this.portalAdminService.requestPasswordReset(payload).subscribe({
        next: () => {
          this.toaster.success('A new OTP has been sent to your email.', 'OTP RESENT');
          this.forgotOtp = '';
          this.time = 60;
          this.startTimer();
          setTimeout(() => this.forgotOtpField?.nativeElement?.focus());
        },
        error: () => this.toaster.error('Failed to resend OTP.', 'TRY AGAIN')
      })
    );
  }

  private resetAllOtpState(): void {
    this.showOtpSection = false;
    this.otp = '';
    this.stopTimer();
    this.time = 0;
    this.storedPassword = '';
    this.storedPin = '';
  }

  ngOnDestroy(): void {
    this.dataSubscriber$.unsubscribe();
  }
}
